import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { TemplateRef } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Content } from 'src/app/models/documents/folder.model';
import { DocumentVersionInfo } from 'src/app/models/documents/document-version.model';
import { SelectableSource } from 'src/app/models/generation/generation.model';
import { LanguageService } from 'src/app/services/language/language.service';
import { formatDecisionTitle } from 'src/app/utils/decision-title-format.util';
import { DriveViewerComposition } from './drive-viewer.composition';
import { ContextMenuMode, DriveItem, ItemType, MoveCallbacks } from './drive-viewer.models';

export class DriveViewerPresentationFacade {
  readonly sourceDropListIds = ['filesSourceList', 'filesSourceGrid', 'filesSourceMobile'];

  constructor(
    readonly composition: DriveViewerComposition,
    private readonly language: LanguageService
  ) {}

  get items(): DriveItem[] { return this.composition.workspace.items; }
  set items(value: DriveItem[]) { this.composition.workspace.items = value; }
  get isRefreshingTree(): boolean { return this.composition.workspace.isRefreshingTree; }
  set isRefreshingTree(value: boolean) { this.composition.workspace.isRefreshingTree = value; }
  get isLoadingFiles(): boolean { return this.composition.workspace.isLoadingFiles; }
  set isLoadingFiles(value: boolean) { this.composition.workspace.isLoadingFiles = value; }
  get currentFolderId(): string { return this.composition.workspace.currentFolderId; }
  set currentFolderId(value: string) { this.composition.workspace.currentFolderId = value; }
  get view(): 'list' | 'grid' { return this.composition.workspace.view; }
  set view(value: 'list' | 'grid') { this.composition.workspace.view = value; }
  get sortKey() { return this.composition.workspace.sortKey; }
  set sortKey(value) { this.composition.workspace.sortKey = value; }
  get sortDir() { return this.composition.workspace.sortDir; }
  set sortDir(value) { this.composition.workspace.sortDir = value; }
  get search(): string { return this.composition.workspace.search; }
  set search(value: string) { this.composition.workspace.search = value; }
  get filterType(): ItemType | 'all' { return this.composition.workspace.filterType; }
  set filterType(value: ItemType | 'all') { this.composition.workspace.filterType = value; }
  get filterOwner() { return this.composition.workspace.filterOwner; }
  set filterOwner(value) { this.composition.workspace.filterOwner = value; }
  get selectedIds(): Set<string> { return this.composition.workspace.selectedIds; }
  get detailsOpen(): boolean { return this.composition.workspace.detailsOpen; }
  set detailsOpen(value: boolean) { this.composition.workspace.detailsOpen = value; }
  get mobileTreeOpen(): boolean { return this.composition.workspace.mobileTreeOpen; }
  set mobileTreeOpen(value: boolean) { this.composition.workspace.mobileTreeOpen = value; }
  get mobileFiltersOpen(): boolean { return this.composition.workspace.mobileFiltersOpen; }
  set mobileFiltersOpen(value: boolean) { this.composition.workspace.mobileFiltersOpen = value; }
  get active(): boolean { return this.composition.workspace.active; }
  set active(value: boolean) { this.composition.workspace.active = value; }
  get previewOpen(): boolean { return this.composition.previewState.previewOpen; }
  set previewOpen(value: boolean) { this.composition.previewState.previewOpen = value; }
  get previewIndex(): number { return this.composition.previewState.previewIndex; }
  set previewIndex(value: number) { this.composition.previewState.previewIndex = value; }
  get previewLoadingIds(): Set<string> { return this.composition.previewState.previewLoadingIds; }
  get previewErrors(): Record<string, string> { return this.composition.previewState.previewErrors; }
  set previewErrors(value: Record<string, string>) { this.composition.previewState.previewErrors = value; }
  get contextMenuVisible(): boolean { return this.composition.moveState.contextMenuVisible; }
  set contextMenuVisible(value: boolean) { this.composition.moveState.contextMenuVisible = value; }
  get menuX(): number { return this.composition.moveState.menuX; }
  get menuY(): number { return this.composition.moveState.menuY; }
  get contextMenuMode(): ContextMenuMode { return this.composition.moveState.contextMenuMode; }
  get contextMenuItem(): DriveItem | undefined { return this.composition.moveState.contextMenuItem; }
  set contextMenuItem(value: DriveItem | undefined) { this.composition.moveState.contextMenuItem = value; }
  get contextMoveExpanded(): boolean { return this.composition.moveState.contextMoveExpanded; }
  get moveDialogFiles(): DriveItem[] { return this.composition.moveState.moveDialogFiles; }
  set moveDialogFiles(value: DriveItem[]) { this.composition.moveState.moveDialogFiles = value; }
  get moveDialogSearch(): string { return this.composition.moveState.moveDialogSearch; }
  set moveDialogSearch(value: string) { this.composition.moveState.moveDialogSearch = value; }
  get moveDialogBusy(): boolean { return this.composition.moveState.moveDialogBusy; }
  get moveDialogError(): string { return this.composition.moveState.moveDialogError; }
  get deleteDialogName(): string { return this.composition.moveState.deleteDialogName; }
  get deleteDialogBusy(): boolean { return this.composition.moveState.deleteDialogBusy; }
  get isDraggingFile(): boolean { return this.composition.moveState.isDraggingFile; }
  set isDraggingFile(value: boolean) { this.composition.moveState.isDraggingFile = value; }
  get dragHoverFolderId(): string | null { return this.composition.moveState.dragHoverFolderId; }
  set dragHoverFolderId(value: string | null) { this.composition.moveState.dragHoverFolderId = value; }
  get newFolderName(): string { return this.composition.moveState.newFolderName; }
  set newFolderName(value: string) { this.composition.moveState.newFolderName = value; }
  get summarySources(): SelectableSource[] { return this.composition.summaryState.summarySources; }
  set summarySources(value: SelectableSource[]) { this.composition.summaryState.summarySources = value; }
  get docVersioningEnabled(): boolean { return this.composition.versionState.docVersioningEnabled; }
  set docVersioningEnabled(value: boolean) { this.composition.versionState.docVersioningEnabled = value; }
  get versionBadges(): Record<string, number> { return this.composition.versionState.versionBadges; }
  get versionHistory(): DocumentVersionInfo[] { return this.composition.versionState.versionHistory; }
  get versionHistoryLoading(): boolean { return this.composition.versionState.versionHistoryLoading; }
  get versionHistoryError(): string { return this.composition.versionState.versionHistoryError; }
  get versionHistoryItem(): DriveItem | undefined { return this.composition.versionState.versionHistoryItem; }
  get createFolderDialogTpl(): TemplateRef<unknown> | undefined { return this.composition.moveState.createFolderDialogTpl; }
  set createFolderDialogTpl(value: TemplateRef<unknown> | undefined) { this.composition.moveState.createFolderDialogTpl = value; }
  get generateSummaryDialogTpl(): TemplateRef<unknown> | undefined { return this.composition.summaryState.generateSummaryDialogTpl; }
  set generateSummaryDialogTpl(value: TemplateRef<unknown> | undefined) { this.composition.summaryState.generateSummaryDialogTpl = value; }
  get versionsDialogTpl(): TemplateRef<unknown> | undefined { return this.composition.versionState.versionsDialogTpl; }
  set versionsDialogTpl(value: TemplateRef<unknown> | undefined) { this.composition.versionState.versionsDialogTpl = value; }

  initialize(): void { this.composition.tree.initialize(); }
  activeChanged(active: boolean): void { this.composition.tree.activeChanged(active); }
  destroy(): void {
    this.composition.autosync.stop();
    this.composition.preview.destroy();
  }
  translate(key: string): string { return this.language.translate(key); }
  displayName(name: string | null | undefined): string {
    if (name === 'Root Folder') return this.translateMyDrive();
    if (name === 'documents.group_by_date') return this.translate('drive.by_date') || 'Por fecha';
    return formatDecisionTitle(name);
  }
  translateMyDrive(): string { return this.translate('drive.my_drive') || 'Mi unidad'; }
  loadSortPreferences(): void { this.composition.tree.loadSortPreferences(); }
  saveSortPreferences(): void { this.composition.tree.saveSortPreferences(); }
  onSortKeyChange(value: typeof this.sortKey): void { this.composition.tree.updateSortKey(value); }
  onSortDirChange(value: typeof this.sortDir): void { this.composition.tree.updateSortDirection(value); }
  loadFolderTree(): void { this.composition.tree.loadFolderTree(); }
  flattenContentNode(node: Content, parentId: string | null, path: string[]): void {
    this.composition.tree.flattenNode(node, parentId, path);
  }
  inferContentKind(node: Content, children: Content[]): 'folder' | 'file' {
    return this.composition.tree.inferKind(node, children);
  }
  resolveNodeDocumentId(node: Content, fallbackId: string): string | undefined {
    return this.composition.tree.resolveDocumentId(node, fallbackId);
  }
  loadByDateSection(): void { this.composition.tree.loadByDateSection(); }
  mergeRootDocuments(): void { this.composition.tree.mergeRootDocuments(); }
  reloadAll(): void { this.composition.tree.reloadAll(); }
  refreshTreePreservingState(silent = false, finished?: () => void): void {
    this.composition.tree.refreshPreservingState(silent, finished);
  }

  get selectedItem(): DriveItem | undefined { return this.composition.selection.selectedItem(); }
  get rootFolder(): DriveItem | undefined { return this.composition.selection.rootFolder(); }
  get currentPath(): string[] { return this.composition.selection.currentPath(); }
  get visibleItems(): DriveItem[] { return this.composition.selection.visibleItems(); }
  get currentPreviewItem(): DriveItem | undefined { return this.composition.selection.currentPreviewItem(); }
  get folderOptions(): DriveItem[] { return this.composition.selection.folderOptions(); }
  get moveDialogFolderOptions(): DriveItem[] { return this.composition.selection.moveDialogFolderOptions(); }
  get folderDropListIds(): string[] { return this.composition.selection.folderDropListIds(); }
  isVirtualFolderId(id: string | null | undefined): boolean { return this.composition.selection.isVirtualFolderId(id); }
  isUnderByDateItem(item: DriveItem): boolean { return this.composition.selection.isUnderByDateItem(item); }
  childTreeItems(parent: string | null): DriveItem[] { return this.composition.selection.childTreeItems(parent); }
  childFolders(parent: string | null): DriveItem[] { return this.composition.selection.childFolders(parent); }
  readonly trackById = (_: number, item: DriveItem): string => item.id;
  hasChildren(parent: string): boolean { return this.composition.selection.hasChildren(parent); }
  hasChildFolders(parent: string): boolean { return this.childFolders(parent).length > 0; }

  get currentFolderTotalCount(): number {
    return this.childTreeItems(this.currentFolderId || null).length;
  }

  get hasActiveFilters(): boolean {
    return !!this.search || this.filterType !== 'all' || this.filterOwner !== 'all';
  }

  get selectionCount(): number { return this.selectedIds.size; }

  get selectionSizeBytes(): number {
    let total = 0;
    this.items.forEach((item) => {
      if (this.selectedIds.has(item.id) && item.type !== 'folder') total += item.size || 0;
    });
    return total;
  }
  dropListId(scope: 'tree' | 'list' | 'grid' | 'mobile', folder: DriveItem): string {
    return this.composition.selection.dropListId(scope, folder);
  }
  canMoveFileToFolder(file: DriveItem, folderId: string): boolean {
    return this.composition.selection.canMoveFileToFolder(file, folderId);
  }
  getSelectedFiles(): DriveItem[] { return this.composition.selection.selectedFiles(); }
  getFileTypeLabel(item: DriveItem): string { return this.composition.selection.fileTypeLabel(item); }

  onDetailsContextMenu(event: MouseEvent): void { this.composition.navigation.onDetailsContextMenu(event); }
  getSelectedItem(): DriveItem | undefined { return this.composition.selection.selectedItem(); }
  onContentContextMenu(event: MouseEvent): void { this.composition.navigation.onContentContextMenu(event); }
  onContentRowContextMenu(event: MouseEvent, item: DriveItem): void {
    this.composition.navigation.onContentRowContextMenu(event, item);
  }
  navigateTo(id: string): void { this.composition.navigation.navigateTo(id); }
  isExpanded(id: string): boolean { return this.composition.navigation.isExpanded(id); }
  toggleExpand(id: string, event?: MouseEvent): void { this.composition.navigation.toggleExpand(id, event); }
  expandPathTo(id: string): void { this.composition.navigation.expandPathTo(id); }
  breadcrumbClick(index: number): void { this.composition.navigation.breadcrumbClick(index); }
  toggleSelect(event: MouseEvent, id: string): void { this.composition.navigation.toggleSelect(event, id); }
  toggleDetails(): void { this.composition.navigation.toggleDetails(); }
  onTreeItemClick(item: DriveItem, event?: MouseEvent): void { this.composition.navigation.onTreeItemClick(item, event); }
  onTreeItemDoubleClick(item: DriveItem, event: MouseEvent): void {
    this.composition.navigation.onTreeItemDoubleClick(item, event);
  }
  onItemNameClick(item: DriveItem, event: MouseEvent): void { this.composition.navigation.onItemNameClick(item, event); }

  openPreview(item: DriveItem): void { this.composition.preview.open(item); }
  closePreview(): void { this.composition.preview.close(); }
  prevPreview(): void { this.composition.preview.previous(); }
  nextPreview(): void { this.composition.preview.next(); }
  isTextPreview(item?: DriveItem): boolean { return this.composition.preview.isText(item); }
  isFramePreview(item?: DriveItem): boolean { return this.composition.preview.isFrame(item); }
  isPreviewLoading(item?: DriveItem): boolean { return this.composition.preview.isLoading(item); }
  getPreviewError(item?: DriveItem): string | null { return this.composition.preview.error(item); }
  getPreviewSafeUrl(item?: DriveItem): SafeResourceUrl | null { return this.composition.preview.safeUrl(item); }
  getPreviewText(item?: DriveItem): string | null { return this.composition.preview.text(item); }
  failPreview(item: DriveItem): void { this.composition.preview.fail(item); }
  getFileIcon(filename: string): string { return this.composition.preview.fileIcon(filename); }
  getIconClass(filename: string): string { return this.composition.preview.iconClass(filename); }
  getItemIcon(item: DriveItem): string { return this.composition.preview.itemIcon(item); }
  getItemIconClass(item: DriveItem): string { return this.composition.preview.itemIconClass(item); }
  mapTypeByExtension(filename: string): ItemType { return this.composition.preview.mapType(filename); }
  ensurePreviewUrlForIndex(index: number): void { this.composition.preview.ensureIndex(index); }
  ensurePreviewUrlForItem(item?: DriveItem): void { this.composition.preview.ensureItem(item); }
  resolvePreviewUrl(item: DriveItem): void { this.composition.preview.resolveUrl(item); }
  fetchTextContent(url: string, item: DriveItem): void { this.composition.preview.fetchTextContent(url, item); }
  fetchPdfBlob(url: string, item: DriveItem): void { this.composition.preview.fetchPdfBlob(url, item); }
  buildViewerUrl(item: DriveItem, url: string): string { return this.composition.preview.buildViewerUrl(item, url); }
  getExtension(item: DriveItem): string | undefined { return this.composition.preview.extension(item); }

  onRootContextMenu(event: MouseEvent): void { this.composition.contextMenu.onRoot(event); }
  onTreeContextMenu(event: MouseEvent, item: DriveItem): void { this.composition.contextMenu.onTree(event, item); }
  openFolderContextMenu(event: MouseEvent, folderId: string): void {
    this.composition.contextMenu.openFolder(event, folderId);
  }
  openFileContextMenu(event: MouseEvent, item: DriveItem): void { this.composition.contextMenu.openFile(event, item); }
  closeContextMenu(): void { this.composition.contextMenu.close(); }
  openCreateFolderDialogFromContext(template: TemplateRef<unknown>): void {
    this.composition.contextMenu.openCreateFolderDialog(template);
  }
  openCreateFolderDialogFromMoveFallback(): void { this.composition.contextMenu.openCreateFolderFromFallback(); }
  confirmCreateFolder(): void { this.composition.contextMenu.confirmCreateFolder(); }
  askAboutContextFile(): void { this.composition.contextMenu.askAboutContextFile(); }
  deleteContextFile(template?: TemplateRef<unknown>): void { this.composition.contextMenu.deleteContextFile(template); }
  confirmDeleteDialog(): void { this.composition.contextMenu.confirmDeleteDialog(); }
  toggleMoveTargets(): void { this.composition.moveDialog.toggleTargets(); }
  openMoveDialogFromContext(template: TemplateRef<unknown>): void { this.composition.moveDialog.open(template); }
  moveDialogFilesLabel(): string { return this.composition.moveDialog.filesLabel(); }
  moveDialogCreateFolder(): void { this.composition.moveDialog.createFolder(); }
  moveDialogMoveToFolder(folder: DriveItem): void { this.composition.moveDialog.moveToFolder(folder); }
  moveContextFilesToFolder(folder: DriveItem): void { this.composition.moveDialog.moveContextFilesToFolder(folder); }
  getContextMoveFiles(): DriveItem[] { return this.composition.moveDialog.contextFiles(); }
  resolveDriveItemDocumentId(item?: DriveItem): string | null { return this.composition.moveExecution.resolveDocumentId(item); }
  confirmDropMove(items: DriveItem[], folder: DriveItem): boolean {
    return this.composition.moveExecution.confirmDropMove(items, folder);
  }
  moveFilesToFolder(files: DriveItem[], folder: DriveItem, silent = false, confirm = false, callbacks?: MoveCallbacks): boolean {
    return this.composition.moveExecution.moveFiles(files, folder, silent, confirm, callbacks);
  }
  moveKey(fileId: string, folderId: string): string { return this.composition.moveExecution.moveKey(fileId, folderId); }
  toDocumentFolderRequestDocumentId(id?: string): string | null { return this.composition.moveExecution.requestDocumentId(id); }
  extractCreatedFolderId(created: unknown): number | null { return this.composition.moveExecution.extractCreatedFolderId(created); }

  onFileDragStarted(item: DriveItem): void { this.composition.dragDrop.fileDragStarted(item); }
  onFileDragEnded(): void { this.composition.dragDrop.fileDragEnded(); }
  onFolderDragEntered(folder: DriveItem): void { this.composition.dragDrop.folderDragEntered(folder); }
  onFolderDragExited(folder: DriveItem): void { this.composition.dragDrop.folderDragExited(folder); }
  isFolderDropCandidate(folder: DriveItem): boolean { return this.composition.dragDrop.isFolderDropCandidate(folder); }
  onNativeFileDragStarted(event: DragEvent, item: DriveItem): void { this.composition.dragDrop.nativeFileDragStarted(event, item); }
  onNativeFileDragEnded(): void { this.composition.dragDrop.nativeFileDragEnded(); }
  onNativeFolderDragOver(event: DragEvent, folder: DriveItem): void { this.composition.dragDrop.nativeFolderDragOver(event, folder); }
  onNativeFolderDragLeave(folder: DriveItem): void { this.composition.dragDrop.nativeFolderDragLeave(folder); }
  onNativeDropOnFolder(event: DragEvent, folder: DriveItem): void { this.composition.dragDrop.nativeDropOnFolder(event, folder); }
  canDropNativeFileOnFolder(folder: DriveItem): boolean { return this.composition.dragDrop.canDropNativeFileOnFolder(folder); }
  onDropOnFolder(event: CdkDragDrop<unknown>, folder: DriveItem): void { this.composition.dragDrop.dropOnFolder(event, folder); }
  startAutosync(): void { this.composition.autosync.start(); }
  stopAutosync(): void { this.composition.autosync.stop(); }

  openGenerateSummaryFromContext(): void { this.composition.summary.openFromContext(); }
  confirmGenerateSummary(): void { this.composition.summary.confirm(); }
  initVersioningFeature(): void { this.composition.versioning.initializeFeature(); }
  showVersionBadge(item: DriveItem): boolean { return this.composition.versioning.showBadge(item); }
  openVersionHistoryFromContext(): void { this.composition.versioning.openFromContext(); }
}
